import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { organizationService } from '../../services/organizationService';
import { licenseService } from '../../services/licenseService';
import Toast from '../common/Toast';
import { usePageTitle } from '../../hooks/usePageTitle';
import { clearLogoCache } from '../../hooks/useLogo';
import { orgTimestamp, formatOrgDate } from '../../utils/dateUtils';

const MyOrganization = () => {
    usePageTitle('My Organization');
    const { user } = useAuth();
    const navigate = useNavigate();
    const [organization, setOrganization] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const [logoPreview, setLogoPreview] = useState(null);

    useEffect(() => {
        fetchOrganizationDetails();
    }, []);

    const fetchOrganizationDetails = async () => {
        try {
            setLoading(true);
            const data = await organizationService.getOrganization(user.org_id);
            setOrganization(data);
            try {
                const sub = await licenseService.getLicenseStatus(user.org_id);
                setSubscriptionStatus(sub || null);
            } catch (_) {
                setSubscriptionStatus(null);
            }

            if (data.logo) {
                setLogoPreview(getImageUrl(data.logo));
            }
        } catch (error) {
            showToast('Failed to load organization details: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        const baseUrl = process.env.REACT_APP_API_URL;
        return imagePath.startsWith('http') ? imagePath : `${baseUrl}/${imagePath}`;
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            showToast('Please select a valid image file (JPEG, PNG, or GIF)', 'error');
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            showToast('Image size should not exceed 2MB', 'error');
            return;
        }

        // Preview the image
        const reader = new FileReader();
        reader.onload = (event) => {
            setLogoPreview(event.target.result);
        };
        reader.readAsDataURL(file);

        // Upload the logo
        uploadLogo(file);
    };

    const uploadLogo = async (file) => {
        try {
            setUploadingLogo(true);
            const formData = new FormData();
            formData.append('logo', file);
            formData.append('org_id', user.org_id);

            await organizationService.updateOrganizationLogo(user.org_id, formData);
            showToast('Logo updated successfully!', 'success');

            // Clear logo cache so sidebar updates immediately
            clearLogoCache(user.org_id);

            // Refresh organization details
            await fetchOrganizationDetails();
        } catch (error) {
            showToast('Failed to upload logo: ' + error.message, 'error');
            setLogoPreview(organization?.logo ? getImageUrl(organization.logo) : null);
        } finally {
            setUploadingLogo(false);
        }
    };

    const showToast = (message, type) => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            0: { label: 'Rejected', class: 'rejected' },
            1: { label: 'Pending', class: 'pending' },
            2: { label: 'Approved', class: 'approved' },
            3: { label: 'Disabled', class: 'disabled' }
        };
        const statusInfo = statusMap[status] || { label: 'Unknown', class: 'unknown' };
        return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
    };

    const getLicenseStatusBadge = (status) => {
        const statusMap = {
            'active': { label: 'Active', class: 'active' },
            'expired': { label: 'Expired', class: 'expired' },
            'grace_period': { label: 'Grace Period', class: 'grace' },
            'inactive': { label: 'Inactive', class: 'inactive' }
        };
        const statusInfo = statusMap[status] || { label: 'Unknown', class: 'unknown' };
        return <span className={`license-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
    };

    if (loading) {
        return (
            <div className="my-organization-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading organization details...</p>
                </div>
            </div>
        );
    }

    if (!organization) {
        return (
            <div className="my-organization-container">
                <div className="error-message">
                    <i className="fas fa-exclamation-circle"></i>
                    <p>Organization not found</p>
                </div>
            </div>
        );
    }

    return (
        <div className="my-organization-container">
            <Toast show={toast.show} message={toast.message} type={toast.type} />

            <div className="page-header">
                <h1>
                    <i className="fas fa-building"></i>
                    My Organization
                </h1>
            </div>

            <div className="organization-profile">
                {/* Logo Section */}
                <div className="logo-section">
                    <div className="logo-container">
                        {logoPreview ? (
                            <img src={logoPreview} alt="Organization Logo" className="org-logo" />
                        ) : (
                            <div className="logo-placeholder">
                                <i className="fas fa-building"></i>
                                <p>No Logo</p>
                            </div>
                        )}
                        {uploadingLogo && (
                            <div className="upload-overlay">
                                <div className="spinner-small"></div>
                                <p>Uploading...</p>
                            </div>
                        )}
                    </div>
                    <div className="logo-actions">
                        <label htmlFor="logo-upload" className="btn btn-primary">
                            <i className="fas fa-upload"></i>
                            {logoPreview ? 'Change Logo' : 'Upload Logo'}
                        </label>
                        <input
                            type="file"
                            id="logo-upload"
                            accept="image/*"
                            onChange={handleLogoChange}
                            style={{ display: 'none' }}
                            disabled={uploadingLogo}
                        />
                        <p className="upload-hint">
                            <i className="fas fa-info-circle"></i>
                            Max 2MB (JPEG, PNG, GIF)
                        </p>
                    </div>
                </div>

                {/* Organization Details */}
                <div className="organization-details">
                    <div className="detail-card">
                        <h3>
                            <i className="fas fa-info-circle"></i>
                            Organization Information
                        </h3>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <label>Organization Name:</label>
                                <span className="value">{organization.name}</span>
                            </div>
                            <div className="detail-item">
                                <label>Category:</label>
                                <span className="value">{organization.category_name || 'N/A'}</span>
                            </div>
                            <div className="detail-item">
                                <label>Status:</label>
                                {getStatusBadge(organization.status)}
                            </div>
                            <div className="detail-item">
                                <label>Country:</label>
                                <span className="value">{organization.country_code || 'N/A'}</span>
                            </div>
                            <div className="detail-item full-width">
                                <label>Description:</label>
                                <span className="value">{organization.description || 'No description provided'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="detail-card">
                        <h3>
                            <i className="fas fa-address-card"></i>
                            Contact Information
                        </h3>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <label>Contact Email:</label>
                                <span className="value">{organization.contact_email || 'N/A'}</span>
                            </div>
                            <div className="detail-item">
                                <label>Contact Phone:</label>
                                <span className="value">{organization.contact_phone || 'N/A'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="detail-card">
                        <h3>
                            <i className="fas fa-key"></i>
                            License & Subscription
                        </h3>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <label>License Status:</label>
                                {getLicenseStatusBadge(organization.license_status)}
                            </div>
                            <div className="detail-item">
                                <label>Subscription Status:</label>
                                {getLicenseStatusBadge(
                                    subscriptionStatus?.is_expired
                                        ? 'expired'
                                        : (subscriptionStatus?.has_active_subscription ? 'active' : 'inactive')
                                )}
                            </div>
                            <div className="detail-item">
                                <label>Staff Usage (Used / Total):</label>
                                <span className="value">
                                    {organization.used_staff_count || 0} / {organization.total_subscription_count || organization.current_license_count || 0}
                                </span>
                            </div>
                            <div className="detail-item">
                                <label>Discount:</label>
                                <span className="value">{organization.discount_percentage || 0}%</span>
                            </div>
                            <div className="detail-item">
                                <label>Subscription Valid Till Date:</label>
                                <span className="value">
                                    {(subscriptionStatus?.end_date || organization.subscription_end_date || organization.license_end_date)
                                        ? formatOrgDate(
                                            subscriptionStatus?.end_date || organization.subscription_end_date || organization.license_end_date,
                                            organization.timezone
                                          )
                                        : ((organization.subscription_status === 'active' || organization.license_status === 'active') ? 'No Expiry' : 'N/A')}
                                </span>
                            </div>
                            <div className="detail-item full-width">
                                <label>Subscription Action:</label>
                                <span className="value">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => navigate('/subscriptions/purchase')}
                                    >
                                        <i className="fas fa-credit-card"></i> Subscribe / Renew
                                    </button>
                                </span>
                            </div>
                            <div className="detail-item">
                                <label>Setup Fee Paid:</label>
                                <span className="value">
                                    {organization.has_paid_setup_fee ? (
                                        <><i className="fas fa-check-circle" style={{ color: 'green' }}></i> Yes</>
                                    ) : (
                                        <><i className="fas fa-times-circle" style={{ color: 'red' }}></i> No</>
                                    )}
                                </span>
                            </div>
                            {organization.license_start_date && (
                                <div className="detail-item">
                                    <label>License Start:</label>
                                    <span className="value">{orgTimestamp(organization, 'license_start_date', organization.timezone, true)}</span>
                                </div>
                            )}
                            {organization.license_end_date && (
                                <div className="detail-item">
                                    <label>License End:</label>
                                    <span className="value">{orgTimestamp(organization, 'license_end_date', organization.timezone, true)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="info-notice">
                        <i className="fas fa-info-circle"></i>
                        <div>
                            <h4>Read-Only Information</h4>
                            <p>
                                As a Client Administrator, you can only update your organization logo.
                                To update other information, please contact support.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .my-organization-container {
                    min-height: 100vh;
                    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                    padding: 2rem;
                }

                .my-organization-container :global(*) {
                    box-sizing: border-box;
                }

                .page-header {
                    margin-bottom: 2rem;
                }

                .page-header h1 {
                    font-size: 2rem;
                    color: #2c3e50;
                    margin-bottom: 0.5rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    font-weight: 700;
                }

                .page-header h1 i {
                    width: 60px;
                    height: 60px;
                    background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 12px;
                    font-size: 1.5rem;
                    box-shadow: 0 4px 15px rgba(20, 184, 166, 0.3);
                }

                .page-header p {
                    color: #64748b;
                    font-size: 1rem;
                    margin: 0;
                    padding-left: 76px;
                }

                .loading-spinner, .error-message {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 4rem 2rem;
                    text-align: center;
                    background: white;
                    border-radius: 20px;
                    max-width: 500px;
                    margin: 4rem auto;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
                }

                .spinner {
                    width: 60px;
                    height: 60px;
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #14b8a6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .error-message i {
                    font-size: 3rem;
                    color: #ef4444;
                    margin-bottom: 1rem;
                }

                .error-message p {
                    color: #64748b;
                    font-size: 1.1rem;
                    margin: 0;
                }

                .organization-profile {
                    display: grid;
                    grid-template-columns: 320px 1fr;
                    gap: 2rem;
                    max-width: 1400px;
                    margin: 0 auto;
                }

                /* Logo Section */
                .logo-section {
                    background: white;
                    border-radius: 20px;
                    padding: 2rem;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
                    height: fit-content;
                    position: sticky;
                    top: 2rem;
                    border-left: 5px solid #14b8a6;
                }

                .logo-container {
                    position: relative;
                    width: 220px;
                    height: 220px;
                    margin: 0 auto 1.5rem;
                    border-radius: 16px;
                    overflow: hidden;
                    border: 3px dashed #cbd5e1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s ease;
                }

                .logo-container:hover {
                    border-color: #14b8a6;
                    box-shadow: 0 4px 15px rgba(20, 184, 166, 0.2);
                }

                .org-logo {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .logo-placeholder {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 1rem;
                    color: #cbd5e1;
                }

                .logo-placeholder i {
                    font-size: 4rem;
                }

                .logo-placeholder p {
                    margin: 0;
                    font-size: 0.9rem;
                    font-weight: 500;
                }

                .upload-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.7);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    color: white;
                }

                .spinner-small {
                    width: 30px;
                    height: 30px;
                    border: 3px solid rgba(255, 255, 255, 0.3);
                    border-top: 3px solid white;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                .upload-overlay p {
                    margin-top: 0.5rem;
                    font-size: 0.9rem;
                }

                .logo-actions {
                    text-align: center;
                }

                .btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    border: none;
                    border-radius: 12px;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    text-decoration: none;
                }

                .btn-primary {
                    background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
                    color: white;
                    box-shadow: 0 4px 15px rgba(20, 184, 166, 0.3);
                }

                .btn-primary:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(20, 184, 166, 0.4);
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .upload-hint {
                    margin-top: 1rem;
                    font-size: 0.85rem;
                    color: #64748b;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                }

                .upload-hint i {
                    color: #14b8a6;
                }

                /* Organization Details */
                .organization-details {
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                .detail-card {
                    background: white;
                    border-radius: 20px;
                    padding: 2rem;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
                    border-left: 5px solid #14b8a6;
                    transition: all 0.3s ease;
                }

                .detail-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
                }

                .detail-card h3 {
                    font-size: 1.3rem;
                    color: #2c3e50;
                    margin: 0 0 1.5rem 0;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding-bottom: 1rem;
                    border-bottom: 2px solid #f1f5f9;
                    font-weight: 700;
                }

                .detail-card h3 i {
                    width: 42px;
                    height: 42px;
                    background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 10px;
                    font-size: 1.1rem;
                }

                .detail-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 1.5rem;
                }

                .detail-item {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .detail-item.full-width {
                    grid-column: span 2;
                }

                .detail-item label {
                    font-weight: 600;
                    color: #64748b;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .detail-item .value {
                    color: #2c3e50;
                    font-size: 1.05rem;
                    word-break: break-word;
                    font-weight: 500;
                }

                /* Status Badges */
                .status-badge, .license-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.5rem 1rem;
                    border-radius: 20px;
                    font-size: 0.85rem;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .status-badge.approved {
                    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
                    color: #155724;
                }

                .status-badge.pending {
                    background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
                    color: #856404;
                }

                .status-badge.rejected {
                    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
                    color: #721c24;
                }

                .status-badge.disabled {
                    background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
                    color: #6c757d;
                }

                .license-badge.active {
                    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
                    color: #155724;
                }

                .license-badge.expired {
                    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
                    color: #721c24;
                }

                .license-badge.grace {
                    background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
                    color: #856404;
                }

                .license-badge.inactive {
                    background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
                    color: #6c757d;
                }

                /* Info Notice */
                .info-notice {
                    background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
                    border-left: 5px solid #14b8a6;
                    padding: 1.5rem;
                    border-radius: 16px;
                    display: flex;
                    gap: 1rem;
                    align-items: flex-start;
                    box-shadow: 0 4px 15px rgba(20, 184, 166, 0.1);
                }

                .info-notice i {
                    font-size: 1.5rem;
                    color: #14b8a6;
                    margin-top: 0.25rem;
                }

                .info-notice h4 {
                    margin: 0 0 0.5rem 0;
                    color: #2c3e50;
                    font-size: 1.1rem;
                    font-weight: 700;
                }

                .info-notice p {
                    margin: 0;
                    color: #475569;
                    line-height: 1.6;
                }

                /* Responsive Design */
                @media (max-width: 1024px) {
                    .organization-profile {
                        grid-template-columns: 1fr;
                    }

                    .logo-section {
                        position: static;
                    }
                }

                @media (max-width: 768px) {
                    .my-organization-container {
                        padding: 1rem;
                    }

                    .page-header h1 {
                        font-size: 1.5rem;
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .page-header h1 i {
                        width: 50px;
                        height: 50px;
                        font-size: 1.3rem;
                    }

                    .page-header p {
                        padding-left: 0;
                        margin-top: 0.5rem;
                    }

                    .detail-grid {
                        grid-template-columns: 1fr;
                    }

                    .detail-item.full-width {
                        grid-column: span 1;
                    }

                    .logo-container {
                        width: 180px;
                        height: 180px;
                    }

                    .detail-card {
                        padding: 1.5rem;
                    }

                    .detail-card h3 {
                        font-size: 1.1rem;
                    }

                    .detail-card h3 i {
                        width: 36px;
                        height: 36px;
                        font-size: 1rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default MyOrganization;
